import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, shortDate, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { useAuth } from "../lib/auth";

const REMINDER_HELP = {
  friendly: "The first, friendly nudge. Sent when an invoice is 1-14 days overdue.",
  firmer: "The firmer follow-up. Sent when payment is still outstanding after the friendly reminder.",
  final: "The final notice. Sent when payment is still outstanding after the firmer follow-up.",
};

const PLACEHOLDERS_HELP = (
  "You can use these placeholders anywhere in the text — the system fills them in when sending:\n" +
  "  {customer_name}       — legal name of the customer\n" +
  "  {contact_first_name}  — first name of their main contact\n" +
  "  {amount}              — how much they owe (formatted with a $)"
);

export default function TemplatesEditor() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [conditions, setConditions] = useState("");
  const [reminders, setReminders] = useState({ friendly: "", firmer: "", final: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = () => api.get("/settings/templates").then(r => {
    setData(r.data);
    setConditions(r.data.conditions);
    setReminders(r.data.reminders);
  });
  useEffect(() => { load(); }, []);

  const canEdit = user?.role === "Director";

  const save = async () => {
    setBusy(true); setMsg(""); setErr("");
    try {
      await api.put("/settings/templates", { conditions, reminders });
      setMsg("Saved. New wording is used from the next quote / reminder onwards.");
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  if (!data) return <div style={{padding: 40, color:"var(--ach-text-2)"}}>Loading…</div>;

  return (
    <div data-testid="templates-editor-page">
      <PageHeader
        title="Email wording &amp; hire conditions"
        subtitle="Change what customers see — no developer needed"
        actions={
          <>
            <button className="ach-btn ach-btn--grey" onClick={()=>nav("/settings")} data-testid="back-to-settings">← Back to Settings</button>
            {canEdit && (
              <button className="ach-btn ach-btn--primary" onClick={save} disabled={busy} data-testid="save-templates">
                {busy ? "Saving…" : "Save changes"}
              </button>
            )}
          </>
        }
      />

      {!canEdit && (
        <div className="ach-card" style={{marginBottom: 16, background:"var(--ach-amber-tint)", borderColor: "var(--ach-amber)"}}>
          <div style={{fontSize:13, color:"var(--ach-amber)"}}>
            You&apos;re signed in as <strong>{user?.role}</strong> — only a Director can save changes here. You can still read what&apos;s set.
          </div>
        </div>
      )}

      {msg && <div className="ach-card" style={{background:"var(--ach-green-tint)", borderColor:"var(--ach-green)", color:"var(--ach-green)", marginBottom:12, fontWeight:600}} data-testid="save-msg">{msg}</div>}
      {err && <div className="ach-card" style={{background:"var(--ach-red-tint)", borderColor:"var(--ach-red)", color:"var(--ach-red)", marginBottom:12, fontWeight:600}}>{err}</div>}

      <div className="ach-card" style={{marginBottom: 16}} data-testid="conditions-card">
        <h2 className="ach-card__title">Standard conditions of hire</h2>
        <div style={{fontSize:12, color:"var(--ach-text-2)", marginBottom:10}}>
          This is the text that appears at the bottom of every quote PDF. Keep it plain.
        </div>
        <textarea
          data-testid="conditions-textarea"
          rows={14}
          value={conditions}
          onChange={(e)=>setConditions(e.target.value)}
          disabled={!canEdit}
          style={{width:"100%", border:"1px solid var(--ach-card-border)", borderRadius:6, padding:12, fontSize:13,
                  fontFamily:"var(--ach-font)", lineHeight:1.5, background: canEdit ? "#fff" : "var(--ach-page-bg)"}}
        />
        {data.conditions_meta?.last_changed_at && (
          <div style={{fontSize:11, color:"var(--ach-text-2)", marginTop:8}}>
            Last changed by <strong>{data.conditions_meta.last_changed_by_name}</strong> on {shortDate(data.conditions_meta.last_changed_at)}
          </div>
        )}
      </div>

      <div className="ach-card">
        <h2 className="ach-card__title">Payment reminder emails</h2>
        <div style={{fontSize:12, color:"var(--ach-text-2)", marginBottom:12}}>
          These three emails are sent from the Invoices &amp; money owed page. They&apos;re sent through Resend once it&apos;s switched on (right now they&apos;re logged).
        </div>
        <div style={{background:"var(--ach-page-bg)", padding:12, borderRadius:8, marginBottom:16, fontFamily:"monospace", fontSize:11.5, whiteSpace:"pre-wrap", color:"var(--ach-text-2)"}}>
          {PLACEHOLDERS_HELP}
        </div>

        {["friendly", "firmer", "final"].map(level => (
          <div key={level} style={{marginBottom: 18}} data-testid={`template-${level}`}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6}}>
              <div style={{fontSize:13, fontWeight:600, textTransform:"capitalize"}}>{level} reminder</div>
              <div style={{fontSize:11, color:"var(--ach-text-2)"}}>{REMINDER_HELP[level]}</div>
            </div>
            <textarea
              data-testid={`textarea-${level}`}
              rows={8}
              value={reminders[level] || ""}
              onChange={(e)=>setReminders({...reminders, [level]: e.target.value})}
              disabled={!canEdit}
              style={{width:"100%", border:"1px solid var(--ach-card-border)", borderRadius:6, padding:12, fontSize:13,
                      fontFamily:"var(--ach-font)", lineHeight:1.5, background: canEdit ? "#fff" : "var(--ach-page-bg)"}}
            />
          </div>
        ))}
        {data.reminders_meta?.last_changed_at && (
          <div style={{fontSize:11, color:"var(--ach-text-2)", marginTop:8}}>
            Last changed by <strong>{data.reminders_meta.last_changed_by_name}</strong> on {shortDate(data.reminders_meta.last_changed_at)}
          </div>
        )}
      </div>
    </div>
  );
}
